import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useLocation, Redirect } from 'react-router-dom';
import { SecureRoute } from '@okta/okta-react';
import { datadogRum } from '@datadog/browser-rum';

import { authRoutes } from './applications/auth/authRoutes';
import { mnoRoutes, getMnoRoutes } from './applications/MnoPortal/mnoRoutes';
import PageNotFound from './404';
import CustomLoginCallback from './CustomLoginCallback';
import { useAuthContext } from './contexts';
import { useRcsVisibility } from '@/hooks';
import { RCS_CAMPAIGN_ENABLED } from '@/constants';
import { IdleWarningCountdownModal } from './components';

const AppWithRouterAccess = () => {
  const location = useLocation();
  const { user, profile } = useAuthContext();
  const rcsEnabled = useRcsVisibility();

  const computeViewName = (routeMatches) => {
    let viewName = '';
    for (let index = 0; index < routeMatches.length; index++) {
      const routeMatch = routeMatches[index];
      const { path } = routeMatch;

      if (!path) {
        return null;
      }

      if (path.startsWith('/')) {
        viewName = path;
      } else {
        viewName += viewName.endsWith('/') ? path : `/${path}`;
      }
    }

    return viewName || '/';
  };

  useEffect(() => {
    const allRoutes = [...authRoutes, ...mnoRoutes];
    const routeMatches = allRoutes.filter((route) => {
      const regex = new RegExp(route.path.replace(/:[^\/]+/g, '\\w+'));
      return regex.test(location.pathname);
    });
    const viewName = routeMatches && computeViewName(routeMatches);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  }, [location.pathname]);

  const accessibleMnoRoutes = useMemo(
    () =>
      getMnoRoutes({
        country: profile?.market,
        features: {
          RCS_CAMPAIGN: RCS_CAMPAIGN_ENABLED,
          RCS_VISIBLE: rcsEnabled,
        },
      }),
    [profile?.market, rcsEnabled]
  );

  return (
    <>
      <IdleWarningCountdownModal />
      <Switch>
        <Route
          exact
          path="/implicit/callback"
          component={CustomLoginCallback}
          key="callback"
        />
        {authRoutes.map(({ path, component, key }, index) => (
          <Route exact path={path} component={component} key={key} />
        ))}
        {accessibleMnoRoutes.map(({ path, component, key }) => (
          <SecureRoute
            exact
            path={path}
            component={component}
            key={key}
            id={key}
          />
        ))}
        <Route path="/404" component={PageNotFound} key="404" />
        <Redirect from="*" to={user ? '/404' : '/login'} />
      </Switch>
    </>
  );
};
export default AppWithRouterAccess;
